import React, { useState, useRef, useEffect, useMemo } from 'react';
import PaymentConfirmationModal from './PaymentConfirmationModal';
import PaymentSuccess from './PaymentSuccess';
import axios from 'axios';

const Booking = () => {
    // --- HELPER FUNCTIONS ---
    // Get current time in IST (Indian Standard Time - UTC+5:30)
    const getISTTime = () => {
        const now = new Date();
        const istOffset = 5.5 * 60 * 60 * 1000; // IST is UTC+5:30
        const utcTime = now.getTime() + (now.getTimezoneOffset() * 60 * 1000);
        return new Date(utcTime + istOffset);
    };

    // Add ordinal suffix to date
    const getOrdinalSuffix = (day) => {
        if (day > 3 && day < 21) return 'th';
        switch (day % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
        }
    };

    // --- STATE MANAGEMENT ---
    // Initialize with current IST date
    const today = getISTTime();
    today.setHours(0, 0, 0, 0);

    const [selectedDate, setSelectedDate] = useState(today);
    const [currentDisplayMonth, setCurrentDisplayMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
    const [selectedTime, setSelectedTime] = useState('10:00 am');
    const [showAllSessions, setShowAllSessions] = useState(false);
    const [selectedService, setSelectedService] = useState(0); // Index of selected service
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [bookingDetails, setBookingDetails] = useState(null);

    // --- STATIC DATA ---
    const services = useMemo(() => [
        {
            name: 'Therapy (1 hour)',
            price: '₹2000',
            duration: '1 hour',
            description: 'Extended therapy session for deeper healing and long-term mental health tools. Ideal for clients needing structured therapeutic work.'
        },
        {
            name: 'Couple Therapy (1 hour)',
            price: '₹3000',
            duration: '1 hour',
            description: 'Improve communication, resolve conflicts, and rebuild emotional connection. For dating, engaged, or married couples facing relational challenges.'
        },
        {
            name: 'Family Therapy (1 hour)',
            price: '₹4000',
            duration: '1 hour',
            description: 'Systemic therapy for families facing emotional, behavioral, or developmental concerns. Helps rebuild harmony and improve family dynamics.'
        },
        {
            name: 'Therapy (45 min)',
            price: '₹1800',
            duration: '45 minutes',
            description: 'For individuals experiencing anxiety, depression, trauma, OCD, or other clinical concerns. Sessions involve diagnosis and tailored therapeutic techniques.'
        },
    ], []);

    const [availableDates, setAvailableDates] = useState([]);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [loadingDates, setLoadingDates] = useState(true);
    const [loadingSlots, setLoadingSlots] = useState(false);

    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

    // Debug logging for backend URL
    // console.log('Booking component - Backend URL:', backendUrl);
    // console.log('Booking component - REACT_APP_BACKEND_URL env:', process.env.REACT_APP_BACKEND_URL);

    // Handle clicks outside dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);



    // Fetch available dates when month changes
    useEffect(() => {
        const fetchAvailableDates = async () => {
            try {
                setLoadingDates(true);
                const year = currentDisplayMonth.getFullYear();
                const month = currentDisplayMonth.getMonth() + 1;
                const url = `${backendUrl}/api/availability/dates/${year}/${month}`;

                                // console.log('Fetching available dates from:', url);
                
                const response = await axios.get(url, {
                    headers: {
                        'Cache-Control': 'no-cache',
                        'Pragma': 'no-cache'
                    }
                });
                
                // console.log('Available dates response:', response.data);

                setAvailableDates(response.data);
            } catch (error) {
                console.error('Failed to fetch available dates:', error);
                console.error('Error details:', {
                    message: error.message,
                    response: error.response?.data,
                    status: error.response?.status,
                    url: error.config?.url
                });
                // Fallback to some default dates if API fails
                setAvailableDates([]);
            } finally {
                setLoadingDates(false);
            }
        };

        fetchAvailableDates();
    }, [currentDisplayMonth, backendUrl]);

    // Fetch available slots when date or service changes
    useEffect(() => {
        if (selectedDate && selectedService !== undefined) {
            const fetchAvailableSlots = async () => {
                try {
                    setLoadingSlots(true);
                    // Format date as YYYY-MM-DD in local timezone
                    const year = selectedDate.getFullYear();
                    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
                    const day = String(selectedDate.getDate()).padStart(2, '0');
                    const dateStr = `${year}-${month}-${day}`;
                    const serviceDuration = getServiceDuration(services[selectedService].name);
                    const url = `${backendUrl}/api/availability/slots`;
                    const params = {
                        date: dateStr,
                        serviceDuration: serviceDuration
                    };

                                        // console.log('Fetching available slots from:', url);
                    // console.log('With params:', params);
                    // console.log('Selected date:', selectedDate, 'Formatted as:', dateStr);

                    const response = await axios.get(url, { 
                        params,
                        headers: {
                            'Cache-Control': 'no-cache',
                            'Pragma': 'no-cache'
                        }
                    });
                    
                    // console.log('Available slots response:', response.data);

                    // Ensure response.data is an array
                    const slots = Array.isArray(response.data) ? response.data : [];
                    setAvailableSlots(slots);

                    // Reset selected time if it's not in the available slots
                    if (slots.length > 0 && !slots.includes(selectedTime)) {
                        setSelectedTime(slots[0]);
                    }
                } catch (error) {
                    console.error('Failed to fetch available slots:', error);
                    console.error('Slots error details:', {
                        message: error.message,
                        response: error.response?.data,
                        status: error.response?.status,
                        url: error.config?.url
                    });
                    // Fallback to some default slots if API fails
                    setAvailableSlots([
                        '10:00 am', '10:30 am', '11:00 am', '11:30 am',
                        '12:00 pm', '12:30 pm', '1:00 pm', '1:30 pm',
                        '2:00 pm', '2:30 pm', '3:00 pm', '3:30 pm',
                        '4:00 pm', '4:30 pm', '5:00 pm'
                    ]);
                } finally {
                    setLoadingSlots(false);
                }
            };

            fetchAvailableSlots();
        }
    }, [selectedDate, selectedService, services, selectedTime, backendUrl]);

    const getServiceDuration = (serviceName) => {
        const durations = {
            'Therapy (45 min)': 45,
            'Therapy (1 hour)': 60,
            'Couple Therapy (1 hour)': 60,
            'Family Therapy (1 hour)': 60,
        };
        return durations[serviceName] || 60;
    };

    // --- CALENDAR LOGIC ---
    const changeMonth = (amount) => {
        const newMonth = new Date(currentDisplayMonth);
        newMonth.setMonth(newMonth.getMonth() + amount);
        setCurrentDisplayMonth(newMonth);
    };

    const renderCalendar = () => {
        const year = currentDisplayMonth.getFullYear();
        const month = currentDisplayMonth.getMonth();
        const firstDayOfMonth = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        const days = [];
        for (let i = 0; i < firstDayOfMonth; i++) {
            days.push(<div key={`empty-${i}`} />);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            // Get current IST time for comparison
            const istTime = getISTTime();
            const todayIST = new Date(istTime);
            todayIST.setHours(0, 0, 0, 0);

            // Create date for comparison
            const currentDate = new Date(year, month, day);
            currentDate.setHours(0, 0, 0, 0);

            const isSelected = selectedDate.getFullYear() === year &&
                selectedDate.getMonth() === month &&
                selectedDate.getDate() === day;

            const isAvailable = availableDates.includes(day);
            const isPastDate = currentDate < todayIST;

            days.push(
                <button
                    key={day}
                    onClick={() => {
                        const newDate = new Date(year, month, day);
                        newDate.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues
                        setSelectedDate(newDate);
                    }}
                    className={`relative flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 mx-auto transition-all duration-200 font-nunito font-light text-sm sm:text-base
                        ${isSelected ? 'bg-black text-white rounded-full font-medium' : 'text-gray-700 hover:bg-gray-50 rounded-full'}
                        ${(!isAvailable || isPastDate) && 'text-gray-300 cursor-not-allowed hover:bg-transparent'}`}
                    disabled={!isAvailable || isPastDate}
                >
                    {day}
                    {isAvailable && !isSelected && !isPastDate && (
                        <div className="absolute bottom-1 w-1 h-1 bg-black rounded-full" />
                    )}
                </button>
            );
        }
        return days;
    };

    // --- HELPERS ---
    const monthName = currentDisplayMonth.toLocaleString('default', { month: 'long' });
    const year = currentDisplayMonth.getFullYear();
    const selectedMonthName = selectedDate.toLocaleString('default', { month: 'long' });
    const selectedDayName = selectedDate.toLocaleString('default', { weekday: 'long' });

    const visibleTimeSlots = showAllSessions ? availableSlots : availableSlots.slice(0, 8);

    // --- HANDLERS ---
    const handleNext = () => {
        setShowPaymentModal(true);
    };

    const handlePaymentSuccess = (paymentDetails) => {
        setBookingDetails(paymentDetails);
        setShowPaymentModal(false);
    };

    const handleCloseSuccess = () => {
        // First close the modal
        setBookingDetails(null);

        // Then reset all form state after a small delay to ensure proper state updates
        setTimeout(() => {
            // Reset all form state to initial values
            const today = getISTTime();
            today.setHours(0, 0, 0, 0);

            setSelectedDate(today);
            setCurrentDisplayMonth(new Date(today.getFullYear(), today.getMonth(), 1));
            setSelectedTime('10:00 am');
            setShowAllSessions(false);
            setSelectedService(0);
            setIsDropdownOpen(false);
            setShowPaymentModal(false); // Ensure payment modal is also closed

            // Clear available dates and slots to force re-fetch
            setAvailableDates([]);
            setAvailableSlots([]);
        }, 100); // Small delay to ensure state updates properly
    };

    return (
        <div id="booking" className="min-h-screen bg-white py-12 sm:py-16 lg:py-20">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
                {/* --- HEADER --- */}
                <div className="text-center mb-12 lg:mb-16">
                    <p className="font-nunito text-xs sm:text-sm lg:text-base font-light text-black tracking-[0.1em] mb-6 uppercase">
                        Book Your Consultation
                    </p>
                    <h1 className="font-cormorant font-semibold text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-gray-900 leading-tight">
                        Take the First Step Towards a Better You
                    </h1>
                </div>

                {/* --- BOOKING CONTENT WRAPPER --- */}
                <div className="bg-white">

                    {/* Desktop Layout */}
                    <div className="hidden lg:block">
                        {/* Headers Row - Only 2 headers now */}
                        <div className="grid grid-cols-12 gap-x-16 xl:gap-x-20 mb-8">
                            <div className="col-span-9">
                                <h2 className="font-cormorant font-semibold text-lg text-black pb-2 border-b border-gray-400 lg:text-xl">
                                    Select a Date and Time
                                </h2>
                            </div>
                            <div className="col-span-3">
                                <h2 className="font-cormorant font-semibold text-lg text-black pb-2 border-b border-gray-400 lg:text-xl">
                                    Service Details
                                </h2>
                            </div>
                        </div>

                        {/* Content Row */}
                        <div className="grid grid-cols-12 gap-x-16 xl:gap-x-20">
                            {/* Calendar Column */}
                            <div className="col-span-5">
                                {/* Calendar Navigation - This is at the same level as "Availability for..." */}
                                <div className="flex items-center justify-between mb-8">
                                    <button onClick={() => changeMonth(-1)} className="p-2 hover:bg-gray-50 rounded-full transition-colors">
                                        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                    </button>
                                    <h3 className="font-cormorant font-light text-xl text-black">{monthName} {year}</h3>
                                    <button onClick={() => changeMonth(1)} className="p-2 hover:bg-gray-50 rounded-full transition-colors">
                                        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                </div>

                                {/* Calendar Grid */}
                                {loadingDates ? (
                                    <div className="flex justify-center py-12">
                                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900"></div>
                                    </div>
                                ) : (
                                    <div>
                                        <div className="grid grid-cols-7 gap-x-3 text-center text-xs text-gray-500 font-nunito mb-4">
                                            <div>Sun</div>
                                            <div>Mon</div>
                                            <div>Tue</div>
                                            <div>Wed</div>
                                            <div>Thu</div>
                                            <div>Fri</div>
                                            <div>Sat</div>
                                        </div>
                                        <div className="grid grid-cols-7 gap-y-3 gap-x-3">
                                            {renderCalendar()}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Time Slots Column */}
                            <div className="col-span-4">
                                {/* Availability text - at same level as calendar month */}
                                <p className="font-nunito font-normal text-base text-black mb-8">
                                    Availability for {selectedDayName} {selectedDate.getDate()} {selectedMonthName}
                                </p>

                                {/* Time Slots Grid */}
                                {loadingSlots ? (
                                    <div className="flex justify-center py-8">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                                    </div>
                                ) : availableSlots.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        No available time slots for this date
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 gap-3 mb-6">
                                        {visibleTimeSlots.map(time => (
                                            <button
                                                key={time}
                                                onClick={() => setSelectedTime(time)}
                                                className={`py-3 px-6 rounded-full border text-sm font-nunito transition-all duration-200 
                                                    ${selectedTime === time
                                                        ? 'bg-black text-white border-black'
                                                        : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'}`}
                                            >
                                                {time}
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {availableSlots.length > 8 && (
                                    <button
                                        onClick={() => setShowAllSessions(!showAllSessions)}
                                        className="font-nunito text-sm text-gray-600 underline underline-offset-2 hover:text-gray-800 transition-colors"
                                    >
                                        {showAllSessions ? 'Show less' : 'Show all sessions'}
                                    </button>
                                )}
                            </div>

                            {/* Service Details Column */}
                            <div className="col-span-3">
                                <div className="space-y-6">
                                    {/* Custom Service Dropdown */}
                                    <div className="relative" ref={dropdownRef}>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsDropdownOpen(!isDropdownOpen);
                                            }}
                                            className="w-full bg-white border border-gray-200 rounded-2xl px-4 py-3.5 pr-12 shadow-sm hover:shadow-md hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all duration-200 cursor-pointer text-left relative"
                                        >
                                            <span className="font-cormorant font-medium text-base text-gray-900">{services[selectedService].name}</span>
                                            <svg className={`w-4 h-4 text-gray-600 transition-transform duration-200 absolute right-4 top-1/2 -translate-y-1/2 ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </button>

                                        {/* Dropdown Options */}
                                        {isDropdownOpen && (
                                            <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-2xl shadow-lg overflow-hidden z-50">
                                                {services.map((service, index) => (
                                                    <button
                                                        key={index}
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                        }}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setSelectedService(index);
                                                            setIsDropdownOpen(false);
                                                        }}
                                                        className={`w-full px-4 py-3 text-left font-cormorant font-medium text-base hover:bg-purple-50 transition-colors duration-150 ${selectedService === index ? 'bg-purple-100' : 'text-gray-700'
                                                            } ${index !== services.length - 1 ? 'border-b border-gray-100' : ''}`}
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <span className={selectedService === index ? 'text-purple-900 font-semibold' : ''}>
                                                                {service.name}
                                                            </span>
                                                            {selectedService === index && (
                                                                <svg className="w-4 h-4 text-purple-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                                </svg>
                                                            )}
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Appointment Info */}
                                    <div className="space-y-3">
                                        <p className="font-nunito text-sm text-gray-700">
                                            <span className="font-semibold">{selectedDayName}</span>, {selectedDate.getDate()}{getOrdinalSuffix(selectedDate.getDate())} {selectedMonthName} {selectedDate.getFullYear()} at {selectedTime}
                                        </p>
                                        <div className="border-t border-gray-200"></div>
                                    </div>

                                    {/* Service Description */}
                                    <div key={`desc-${selectedService}`}>
                                        <p className="font-cormorant text-sm lg:text-base font-semibold text-gray-600 leading-relaxed">
                                            {services[selectedService].description}
                                        </p>
                                    </div>
                                </div>

                                <button
                                    onClick={handleNext}
                                    className="w-full bg-black hover:bg-gray-900 text-white font-nunito text-base py-3.5 rounded-full transition-all duration-300 transform hover:scale-[1.02] mt-8"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Mobile & Medium Layout */}
                    <div className="lg:hidden">
                        <div className="grid grid-cols-1 md:grid-cols-2 md:gap-x-10">
                            {/* Left Column: Calendar */}
                            <div className="mb-10 md:mb-0">
                                <h2 className="font-cormorant font-semibold text-lg text-black pb-2 border-b border-gray-400 lg:text-xl">
                                    Select a Date and Time
                                </h2>
                                <div className="mt-8">
                                    <div className="flex items-center justify-between mb-6">
                                        <button onClick={() => changeMonth(-1)} className="p-2 hover:bg-gray-50 rounded-full transition-colors">
                                            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                            </svg>
                                        </button>
                                        <h3 className="font-nunito font-normal text-sm sm:text-base text-gray-900">{monthName} {year}</h3>
                                        <button onClick={() => changeMonth(1)} className="p-2 hover:bg-gray-50 rounded-full transition-colors">
                                            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-7 gap-x-1 text-center text-xs text-gray-500 font-nunito mb-3">
                                        <div>Sun</div><div>Mon</div><div>Tue</div><div>Wed</div><div>Thu</div><div>Fri</div><div>Sat</div>
                                    </div>
                                    <div className="grid grid-cols-7 gap-y-2 gap-x-1">{renderCalendar()}</div>
                                </div>
                            </div>

                            {/* Right Column: Time Slots */}
                            <div>
                                <div className="pb-4 border-b border-gray-200 md:hidden">
                                    {/* Empty div to maintain border on mobile */}
                                </div>
                                <p className="font-nunito font-normal text-sm text-black mt-8 mb-6">
                                    Availability for {selectedDayName} {selectedDate.getDate()} {selectedMonthName}
                                </p>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-2 gap-3 mb-6">
                                    {visibleTimeSlots.map(time => (
                                        <button key={time} onClick={() => setSelectedTime(time)}
                                            className={`py-2.5 sm:py-3 px-3 sm:px-4 rounded-full border text-xs sm:text-sm font-nunito transition-all duration-200 
                                                ${selectedTime === time
                                                    ? 'bg-black text-white border-black'
                                                    : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'}`}>
                                            {time}
                                        </button>
                                    ))}
                                </div>
                                {availableSlots.length > 8 && (
                                    <button onClick={() => setShowAllSessions(!showAllSessions)}
                                        className="font-nunito text-xs sm:text-sm text-gray-600 underline underline-offset-2 hover:text-gray-800 transition-colors">
                                        {showAllSessions ? 'Show less' : 'Show all sessions'}
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Service Details Section (Full width below) */}
                        <div className="mt-10 pt-8 border-t border-gray-200">
                            <h2 className="font-cormorant font-semibold text-lg text-black pb-2 border-b border-gray-400 lg:text-xl">
                                Service Details
                            </h2>
                            <div className="mt-8">
                                <div className="space-y-6 mb-8">
                                    {/* Custom Service Dropdown */}
                                    <div className="relative" ref={dropdownRef}>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsDropdownOpen(!isDropdownOpen);
                                            }}
                                            className="w-full bg-white border border-gray-200 rounded-2xl px-3 sm:px-4 py-3 sm:py-3.5 pr-10 sm:pr-12 shadow-sm hover:shadow-md hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all duration-200 cursor-pointer text-left relative"
                                        >
                                            <span className="font-cormorant font-medium text-base sm:text-lg text-gray-900">{services[selectedService].name}</span>
                                            <svg className={`w-4 h-4 text-gray-600 transition-transform duration-200 absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </button>

                                        {/* Dropdown Options */}
                                        {isDropdownOpen && (
                                            <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-2xl shadow-lg overflow-hidden z-50 max-h-64 overflow-y-auto">
                                                {services.map((service, index) => (
                                                    <button
                                                        key={index}
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                        }}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setSelectedService(index);
                                                            setIsDropdownOpen(false);
                                                        }}
                                                        className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 text-left font-cormorant font-medium text-base sm:text-lg hover:bg-purple-50 transition-colors duration-150 ${selectedService === index ? 'bg-purple-100' : 'text-gray-700'
                                                            } ${index !== services.length - 1 ? 'border-b border-gray-100' : ''}`}
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <span className={selectedService === index ? 'text-purple-900 font-semibold' : ''}>
                                                                {service.name}
                                                            </span>
                                                            {selectedService === index && (
                                                                <svg className="w-4 h-4 text-purple-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                                </svg>
                                                            )}
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Appointment Info */}
                                    <div className="space-y-3">
                                        <p className="font-nunito text-xs sm:text-sm text-gray-700">
                                            <span className="font-semibold">{selectedDayName}</span>, {selectedDate.getDate()}{getOrdinalSuffix(selectedDate.getDate())} {selectedMonthName} {selectedDate.getFullYear()} at {selectedTime}
                                        </p>
                                        <div className="border-t border-gray-200"></div>
                                    </div>

                                    {/* Service Description */}
                                    <div key={`desc-mobile-${selectedService}`}>
                                        <p className="font-cormorant text-base sm:text-lg text-gray-700">
                                            {services[selectedService].description}
                                        </p>
                                    </div>
                                </div>
                                {/* Next button always below on smaller screens */}
                                <button
                                    onClick={handleNext}
                                    className="w-full bg-black hover:bg-gray-900 text-white font-nunito text-sm sm:text-base py-3 sm:py-3.5 rounded-full transition-all duration-300 transform hover:scale-[1.02]"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Payment Confirmation Modal */}
            <PaymentConfirmationModal
                isOpen={showPaymentModal}
                onClose={() => setShowPaymentModal(false)}
                bookingDetails={{
                    selectedDate,
                    selectedTime,
                    service: services[selectedService]
                }}
                onPaymentSuccess={handlePaymentSuccess}
            />

            {/* Payment Success Modal */}
            {bookingDetails && (
                <PaymentSuccess
                    bookingDetails={bookingDetails}
                    onClose={handleCloseSuccess}
                />
            )}
        </div>
    );
};

export default Booking; 